import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Wallet, Plus, Upload, FileText, Pencil, Trash2, X, AlertCircle, Check,
} from 'lucide-react';
import { expensesAPI, Expense, ExpenseInput } from '../services/api';

const PAYMENT_MODES = ['Cash', 'Cheque', 'Bank Transfer', 'UPI', 'NEFT', 'RTGS', 'IMPS', 'Card', 'Other'];

const inr = (n: number) =>
  n.toLocaleString('en-IN', { maximumFractionDigits: 2 });

const dmy = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const today = () => new Date().toISOString().slice(0, 10);

type FormState = {
  billNumber: string;
  billDate: string;
  vendorName: string;
  vendorGstin: string;
  description: string;
  category: string;
  taxableAmount: string;
  cgstAmount: string;
  sgstAmount: string;
  igstAmount: string;
  tdsDeducted: string;
  totalAmount: string;
  paymentMode: string;
  notes: string;
};

const emptyForm = (): FormState => ({
  billNumber: '', billDate: today(), vendorName: '', vendorGstin: '', description: '',
  category: '', taxableAmount: '', cgstAmount: '', sgstAmount: '', igstAmount: '',
  tdsDeducted: '', totalAmount: '', paymentMode: 'Bank Transfer', notes: '',
});

const num = (s: string) => (s === '' ? 0 : Number(s));

const Field: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({ label, children, className }) => (
  <div className={className}>
    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">{label}</label>
    {children}
  </div>
);

const inputCls =
  'w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';

const Expenses: React.FC = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [file, setFile] = useState<File | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [existingFileUrl, setExistingFileUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await expensesAPI.getAll();
      setExpenses(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (key: keyof FormState, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Auto-sum total when taxable/tax fields change and the user hasn't typed a
  // custom total for the current edit yet (kept editable afterwards).
  const autoTotal = () => {
    const t = num(form.taxableAmount) + num(form.cgstAmount) + num(form.sgstAmount) + num(form.igstAmount);
    set('totalAmount', t ? String(t) : '');
  };

  const resetForm = () => {
    setForm(emptyForm());
    setFile(null);
    setEditingId(null);
    setExistingFileUrl(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const startEdit = (e: Expense) => {
    setEditingId(e._id);
    setExistingFileUrl(e.billFileUrl);
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
    setForm({
      billNumber: e.billNumber,
      billDate: e.billDate.slice(0, 10),
      vendorName: e.vendorName,
      vendorGstin: e.vendorGstin || '',
      description: e.description || '',
      category: e.category || '',
      taxableAmount: e.taxableAmount ? String(e.taxableAmount) : '',
      cgstAmount: e.cgstAmount ? String(e.cgstAmount) : '',
      sgstAmount: e.sgstAmount ? String(e.sgstAmount) : '',
      igstAmount: e.igstAmount ? String(e.igstAmount) : '',
      tdsDeducted: e.tdsDeducted ? String(e.tdsDeducted) : '',
      totalAmount: String(e.totalAmount),
      paymentMode: e.paymentMode || 'Bank Transfer',
      notes: e.notes || '',
    });
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setError('');
    setSuccess('');
    if (!form.billNumber.trim() || !form.vendorName.trim() || form.totalAmount === '') {
      setError('Bill number, vendor name and total amount are required.');
      return;
    }
    const payload: ExpenseInput = {
      billNumber: form.billNumber.trim(),
      billDate: form.billDate,
      vendorName: form.vendorName.trim(),
      vendorGstin: form.vendorGstin.trim(),
      description: form.description.trim(),
      category: form.category.trim(),
      taxableAmount: num(form.taxableAmount),
      cgstAmount: num(form.cgstAmount),
      sgstAmount: num(form.sgstAmount),
      igstAmount: num(form.igstAmount),
      tdsDeducted: num(form.tdsDeducted),
      totalAmount: num(form.totalAmount),
      paymentMode: form.paymentMode,
      notes: form.notes.trim(),
    };
    setSaving(true);
    try {
      if (editingId) {
        await expensesAPI.update(editingId, payload, file);
        setSuccess('Expense updated.');
      } else {
        await expensesAPI.create(payload, file);
        setSuccess('Expense added.');
      }
      resetForm();
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e: Expense) => {
    if (!window.confirm(`Delete bill ${e.billNumber} from ${e.vendorName}?`)) return;
    try {
      await expensesAPI.delete(e._id);
      if (editingId === e._id) resetForm();
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete expense');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="p-3 bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-lg">
          <Wallet className="h-7 w-7 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Expenses</h1>
          <p className="text-slate-600">Upload vendor bills and manage your purchases</p>
        </div>
      </div>

      {error && (
        <div className="flex items-center space-x-2 bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center space-x-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl">
          <Check className="h-5 w-5" /><span>{success}</span>
        </div>
      )}

      {/* Form card */}
      <div ref={formRef} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-slate-900">
            {editingId ? 'Edit Bill' : 'Add Bill'}
          </h2>
          {editingId && (
            <button onClick={resetForm} className="flex items-center space-x-1 text-sm text-slate-500 hover:text-slate-800">
              <X className="h-4 w-4" /><span>Cancel edit</span>
            </button>
          )}
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Bill Number *">
              <input className={inputCls} value={form.billNumber} onChange={(e) => set('billNumber', e.target.value)} placeholder="e.g. INV-4521" />
            </Field>
            <Field label="Bill Date *">
              <input type="date" className={inputCls} value={form.billDate} onChange={(e) => set('billDate', e.target.value)} />
            </Field>
            <Field label="Vendor Name *">
              <input className={inputCls} value={form.vendorName} onChange={(e) => set('vendorName', e.target.value)} placeholder="Who you bought from" />
            </Field>
            <Field label="Vendor GSTIN">
              <input className={inputCls} value={form.vendorGstin} onChange={(e) => set('vendorGstin', e.target.value)} placeholder="29ABCDE1234F1Z5" />
            </Field>
            <Field label="Description">
              <input className={inputCls} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="What was bought" />
            </Field>
            <Field label="Category">
              <input className={inputCls} value={form.category} onChange={(e) => set('category', e.target.value)} placeholder="Hardware, Travel…" />
            </Field>

            <Field label="Taxable Amount">
              <input type="number" step="0.01" min="0" className={inputCls} value={form.taxableAmount} onChange={(e) => set('taxableAmount', e.target.value)} onBlur={autoTotal} placeholder="0.00" />
            </Field>
            <Field label="CGST Amount">
              <input type="number" step="0.01" min="0" className={inputCls} value={form.cgstAmount} onChange={(e) => set('cgstAmount', e.target.value)} onBlur={autoTotal} placeholder="0.00" />
            </Field>
            <Field label="SGST Amount">
              <input type="number" step="0.01" min="0" className={inputCls} value={form.sgstAmount} onChange={(e) => set('sgstAmount', e.target.value)} onBlur={autoTotal} placeholder="0.00" />
            </Field>
            <Field label="IGST Amount">
              <input type="number" step="0.01" min="0" className={inputCls} value={form.igstAmount} onChange={(e) => set('igstAmount', e.target.value)} onBlur={autoTotal} placeholder="0.00" />
            </Field>
            <Field label="TDS Deducted">
              <input type="number" step="0.01" min="0" className={inputCls} value={form.tdsDeducted} onChange={(e) => set('tdsDeducted', e.target.value)} placeholder="0.00" />
            </Field>
            <Field label="Total Amount *">
              <input type="number" step="0.01" min="0" className={inputCls} value={form.totalAmount} onChange={(e) => set('totalAmount', e.target.value)} placeholder="0.00" />
            </Field>

            <Field label="Payment Mode">
              <select className={inputCls} value={form.paymentMode} onChange={(e) => set('paymentMode', e.target.value)}>
                {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Notes" className="md:col-span-2">
              <input className={inputCls} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Optional notes" />
            </Field>
          </div>

          {/* File upload */}
          <Field label="Bill File (PDF / JPG / PNG)">
            <div className="flex flex-wrap items-center gap-3">
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                id="billFileInput"
              />
              <label htmlFor="billFileInput" className="flex items-center space-x-2 px-4 py-2.5 rounded-xl border border-dashed border-slate-300 bg-slate-50 text-sm font-medium text-slate-600 cursor-pointer hover:border-indigo-400 hover:text-indigo-600 transition-colors">
                <Upload className="h-4 w-4" />
                <span>{file ? file.name : 'Choose file'}</span>
              </label>
              {existingFileUrl && !file && (
                <a href={existingFileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1 text-sm text-indigo-600 hover:text-indigo-800 font-medium">
                  <FileText className="h-4 w-4" /><span>Current bill</span>
                </a>
              )}
            </div>
          </Field>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center space-x-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow-lg shadow-indigo-500/30 hover:opacity-90 transition-opacity disabled:opacity-40"
            >
              <Plus className="h-4 w-4" />
              <span>{saving ? 'Saving…' : editingId ? 'Update Bill' : 'Add Bill'}</span>
            </button>
          </div>
        </form>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-16 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-3 border-indigo-200 border-t-indigo-600" />
          </div>
        ) : expenses.length === 0 ? (
          <div className="p-16 text-center text-slate-500">
            <Wallet className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            No bills uploaded yet. Add your first one above.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  <th className="px-4 py-3 whitespace-nowrap">Bill No.</th>
                  <th className="px-4 py-3 whitespace-nowrap">Date</th>
                  <th className="px-4 py-3 whitespace-nowrap">Vendor</th>
                  <th className="px-4 py-3 whitespace-nowrap">GSTIN</th>
                  <th className="px-4 py-3 whitespace-nowrap">Description</th>
                  <th className="px-4 py-3 whitespace-nowrap text-right">Total</th>
                  <th className="px-4 py-3 whitespace-nowrap">Bill</th>
                  <th className="px-4 py-3 whitespace-nowrap text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {expenses.map((e) => (
                  <tr key={e._id} className={`hover:bg-slate-50/60 ${editingId === e._id ? 'bg-indigo-50/40' : ''}`}>
                    <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap">{e.billNumber}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{dmy(e.billDate)}</td>
                    <td className="px-4 py-3 text-slate-900 whitespace-nowrap">{e.vendorName}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{e.vendorGstin || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{e.description || '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900 whitespace-nowrap">₹{inr(e.totalAmount)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {e.billFileUrl ? (
                        <a href={e.billFileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-1 text-indigo-600 hover:text-indigo-800 font-medium">
                          <FileText className="h-4 w-4" /><span>View</span>
                        </a>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="inline-flex items-center gap-2">
                        <button onClick={() => startEdit(e)} className="p-2 rounded-lg text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors" title="Edit">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => remove(e)} className="p-2 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Expenses;
